import {
  type CapabilitiesResponse,
  CEO_METRIC_IDS,
  type CeoBreakdownQuery,
  type CeoBreakdownResponse,
  type CeoQuery,
  type CeoSummaryQuery,
  type CeoSummaryResponse,
  type CeoTrendsResponse,
  CONTRACT_VERSION,
  CRM_PANEL_IDS,
  type CrmQuery,
  type CrmResponse,
  capabilitiesResponseSchema,
  ceoBreakdownResponseSchema,
  ceoSummaryResponseSchema,
  ceoTrendsResponseSchema,
  crmResponseSchema,
  type DataSetId,
  errorResponseSchema,
  FUNNEL_CARD_IDS,
  FUNNEL_GAP_IDS,
  FUNNEL_STAGE_IDS,
  type FunnelQuery,
  type FunnelResponse,
  funnelResponseSchema,
  type SessionUser,
  sessionUserSchema,
} from '@sr-ceo/contracts';
import type { z } from 'zod';

/** The session is missing or expired: the page should ask the user to sign in again. */
export class Unauthenticated extends Error {}
/** The bridge (or the proxy in front of it) could not be reached or failed. */
export class BridgeUnavailable extends Error {}
/** The bridge answered with an error: a 5xx may pass on its own; a 4xx stays until the request changes. */
export class BridgeError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function call<T>(path: string, schema: z.ZodType<T>, init: RequestInit = {}): Promise<T> {
  let resp: Response;
  try {
    resp = await fetch(path, {
      ...init,
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', ...init.headers },
    });
  } catch {
    throw new BridgeUnavailable('The dashboard service cannot be reached.');
  }
  const body: unknown = await resp.json().catch(() => undefined);
  const err = errorResponseSchema.safeParse(body);
  const message = err.success ? err.data.error.message : `Dashboard service error (HTTP ${resp.status}).`;
  // A 401 from the sign-in call itself is a wrong password, not an expired session.
  if (resp.status === 401 && path !== '/auth/login') {
    throw new Unauthenticated(message);
  }
  if (resp.status >= 502 && !err.success) {
    throw new BridgeUnavailable('The dashboard service cannot be reached.');
  }
  if (!resp.ok) {
    throw new BridgeError(message, resp.status);
  }
  if (body === undefined) {
    // A 200 whose body broke off part-way (or isn't JSON): the answer was lost, as if none came.
    throw new BridgeUnavailable('The dashboard service cannot be reached.');
  }
  return schema.parse(body);
}

// Fixture mode (VITE_FIXTURES=1): no bridge, no sign-in, no real data. For UI work and
// layout tests only; the header says so, and every card is Unavailable.
const FIXTURES = import.meta.env.VITE_FIXTURES === '1';
const FIXTURE_REASON = 'Fixture mode: no bridge connected, so no data.';
const MILLIS = /\.\d+Z$/;
const fixtureNow = () => new Date().toISOString().replace(MILLIS, 'Z');
const fixtures = {
  user: { username: 'fixture', scope: [] } satisfies SessionUser,
  capabilities: (dataSet: DataSetId): CapabilitiesResponse => ({
    contractVersion: CONTRACT_VERSION,
    environment: 'fixture mode',
    dataSet,
    dataSets: [
      { id: 'ds', label: 'DS gold' },
      { id: 'ours', label: 'Our gold' },
    ],
    dataSource: 'No data source',
    snapshotId: null,
    dataAsOf: null,
    freshness: { status: 'unknown' },
    filters: { country: [], source: [], game: [], currency: [] },
    metrics: CEO_METRIC_IDS.map((metricId) => ({ metricId, status: 'unavailable' as const })),
    limits: { maxRangeDays: 366 },
  }),
  ceoSummary: (q: CeoSummaryQuery): CeoSummaryResponse => ({
    contractVersion: CONTRACT_VERSION,
    requestId: 'fixture',
    generatedAt: fixtureNow(),
    snapshotId: null,
    dataAsOf: null,
    freshness: { status: 'unknown' },
    filters: { ...q, timezone: 'UTC' },
    status: 'unavailable',
    missingInputs: [],
    results: CEO_METRIC_IDS.map((metricId) => ({
      metricId,
      definitionVersion: null,
      status: 'unavailable' as const,
      value: null,
      numerator: null,
      denominator: null,
      unit: 'count' as const,
      currency: null,
      basis: null,
      freshness: { status: 'unknown' as const },
      missingInputs: [{ code: 'fixture', description: FIXTURE_REASON }],
    })),
  }),
};

const fixtureFunnel = (q: FunnelQuery): FunnelResponse => {
  const none = <I extends string>(id: I, unit: 'count' | 'ratio' | 'seconds') => ({
    id,
    status: 'unavailable' as const,
    value: null,
    numerator: null,
    denominator: null,
    unit,
    basis: null,
    missingInputs: [{ code: 'fixture', description: FIXTURE_REASON }],
  });
  return {
    contractVersion: CONTRACT_VERSION,
    requestId: 'fixture',
    generatedAt: fixtureNow(),
    snapshotId: null,
    dataAsOf: null,
    freshness: { status: 'unknown' },
    filters: { ...q, timezone: 'UTC' },
    definitionVersion: null,
    cards: FUNNEL_CARD_IDS.map((id) => none(id, id === 'trackingLinkOpens' ? 'count' : 'ratio')),
    stages: FUNNEL_STAGE_IDS.map((id) => none(id, 'count')),
    gaps: FUNNEL_GAP_IDS.map((id) => none(id, 'seconds')),
    unavailable: {
      dropOffBySource: [{ code: 'fixture', description: FIXTURE_REASON }],
      actions: [{ code: 'fixture', description: FIXTURE_REASON }],
    },
  };
};

const fixtureEnvelope = <Q extends object>(q: Q) => ({
  contractVersion: CONTRACT_VERSION as typeof CONTRACT_VERSION,
  requestId: 'fixture',
  generatedAt: fixtureNow(),
  snapshotId: null,
  dataAsOf: null,
  freshness: { status: 'unknown' as const },
  filters: { ...q, timezone: 'UTC' as const },
});
const fixtureReason = [{ code: 'fixture', description: FIXTURE_REASON }];
const fixtureSeries = { status: 'unavailable' as const, values: [], basis: null, missingInputs: fixtureReason };

// Only the filters that are set: an absent key means "All" (or all time, for from).
const query = (q: object) =>
  new URLSearchParams(Object.entries(q).filter((e): e is [string, string] => typeof e[1] === 'string'));

const live = {
  me: (): Promise<SessionUser> => call('/auth/me', sessionUserSchema),
  login: (username: string, password: string): Promise<SessionUser> =>
    call('/auth/login', sessionUserSchema, { method: 'POST', body: JSON.stringify({ username, password }) }),
  logout: async () => {
    const resp = await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin' });
    if (!resp.ok) {
      throw new Error(`sign-out failed: HTTP ${resp.status}`);
    }
  },
  capabilities: (dataSet: DataSetId): Promise<CapabilitiesResponse> =>
    call(`/reporting/v1/capabilities?dataSet=${dataSet}`, capabilitiesResponseSchema),
  ceoSummary: (q: CeoSummaryQuery): Promise<CeoSummaryResponse> =>
    call(`/reporting/v1/ceo/summary?${query(q)}`, ceoSummaryResponseSchema),
  funnel: (q: FunnelQuery): Promise<FunnelResponse> => call(`/reporting/v1/funnel?${query(q)}`, funnelResponseSchema),
  crm: (q: CrmQuery): Promise<CrmResponse> => call(`/reporting/v1/crm?${query(q)}`, crmResponseSchema),
  ceoTrends: (q: CeoQuery): Promise<CeoTrendsResponse> =>
    call(`/reporting/v1/ceo/trends?${query(q)}`, ceoTrendsResponseSchema),
  ceoBreakdowns: (q: CeoBreakdownQuery): Promise<CeoBreakdownResponse> =>
    call(`/reporting/v1/ceo/breakdowns?${query(q)}`, ceoBreakdownResponseSchema),
};

export const api: typeof live = FIXTURES
  ? {
      me: async () => fixtures.user,
      login: async () => fixtures.user,
      logout: async () => undefined,
      capabilities: async (dataSet) => fixtures.capabilities(dataSet),
      ceoSummary: async (q) => fixtures.ceoSummary(q),
      funnel: async (q) => fixtureFunnel(q),
      crm: async (q) => ({
        ...fixtureEnvelope(q),
        definitionVersion: null,
        retention: { status: 'unavailable', basis: null, missingInputs: fixtureReason, cohorts: [] },
        health: {
          status: 'unavailable',
          basis: null,
          missingInputs: fixtureReason,
          rule: null,
          players: null,
          dormant: null,
          signals: [],
        },
        unavailable: Object.fromEntries(CRM_PANEL_IDS.map((id) => [id, fixtureReason])) as CrmResponse['unavailable'],
      }),
      ceoTrends: async (q) => ({
        ...fixtureEnvelope(q),
        days: [],
        series: { deposits: fixtureSeries, ggr: fixtureSeries, ngr: fixtureSeries },
      }),
      ceoBreakdowns: async (q) => ({
        ...fixtureEnvelope(q),
        status: 'unavailable',
        basis: null,
        missingInputs: fixtureReason,
        rows: [],
      }),
    }
  : live;
